import _classPrivateMethodInitSpec from "@babel/runtime/helpers/classPrivateMethodInitSpec";
import _classPrivateFieldInitSpec from "@babel/runtime/helpers/classPrivateFieldInitSpec";
import _classPrivateFieldGet from "@babel/runtime/helpers/classPrivateFieldGet2";
import _assertClassBrand from "@babel/runtime/helpers/assertClassBrand";
import _classPrivateFieldSet from "@babel/runtime/helpers/classPrivateFieldSet2";
import { locationsAreEqual } from "history";
import { HttpAbortError } from "@next-core/http";
import { clearExpressionASTCache, clearFunctionASTCache } from "@next-core/cook";
import { uniqueId } from "lodash";
import { getHistory } from "../history.js";
import { getDataStores, postAsyncRender, renderRoutes } from "./Renderer.js";
import { DataStore } from "./data/DataStore.js";
import { clearResolveCache } from "./data/resolveData.js";
import { mountTree, unmountTree } from "./mount.js";
import { isOutsideApp, matchStoryboard } from "./matchStoryboard.js";
import { registerStoryboardFunctions } from "./compute/StoryboardFunctions.js";
import { RendererContext } from "./RendererContext.js";
import { applyMode, applyTheme, getLocalAppsTheme, setMode, setTheme, setThemeVariant } from "../themeAndMode.js";
import { _internalApiGetAppInBootstrapData, getRuntime, hooks } from "./Runtime.js";
import { getPageInfo } from "../getPageInfo.js";
import { resetAllComputedMarks } from "./compute/markAsComputed.js";
import { handleHttpError, isUnauthenticatedError } from "../handleHttpError.js";
import { abortPendingRequest, initAbortController } from "./abortController.js";
import { setLoginStateCookie } from "../setLoginStateCookie.js";
import { registerCustomTemplates } from "./registerCustomTemplates.js";
import { fulfilStoryboard } from "./fulfilStoryboard.js";
import { RenderTag } from "./enums.js";
import { insertPreviewRoutes } from "./insertPreviewRoutes.js";
import { devtoolsHookEmit } from "./devtools.js";
import { setUIVersion } from "../setUIVersion.js";
import { setAppVariable } from "../setAppVariable.js";
import { setWatermark } from "../setWatermark.js";
import { clearMatchedRoutes } from "./routeMatchedMap.js";
import { ErrorNode, PageError } from "./ErrorNode.js";
import { resetReloadForError, shouldReloadForError } from "../shouldReloadForError.js";
import { computeRealValue } from "./compute/computeRealValue.js";
var _storyboards = /*#__PURE__*/new WeakMap();
var _rendering = /*#__PURE__*/new WeakMap();
var _nextRender = /*#__PURE__*/new WeakMap();
var _runtimeContext = /*#__PURE__*/new WeakMap();
var _rendererContext = /*#__PURE__*/new WeakMap();
var _rendererContextTrashCan = /*#__PURE__*/new WeakMap();
var _redirectCount = /*#__PURE__*/new WeakMap();
var _renderId = /*#__PURE__*/new WeakMap();
var _currentApp = /*#__PURE__*/new WeakMap();
var _previousApp = /*#__PURE__*/new WeakMap();
var _navConfig = /*#__PURE__*/new WeakMap();
var _bootstrapFailed = /*#__PURE__*/new WeakMap();
var _Router_brand = /*#__PURE__*/new WeakSet();
export class Router {
  constructor(storyboards) {
    _classPrivateMethodInitSpec(this, _Router_brand);
    _classPrivateFieldInitSpec(this, _storyboards, void 0);
    _classPrivateFieldInitSpec(this, _rendering, false);
    _classPrivateFieldInitSpec(this, _nextRender, void 0);
    _classPrivateFieldInitSpec(this, _runtimeContext, void 0);
    _classPrivateFieldInitSpec(this, _rendererContext, void 0);
    _classPrivateFieldInitSpec(this, _rendererContextTrashCan, new Set());
    _classPrivateFieldInitSpec(this, _redirectCount, 0);
    _classPrivateFieldInitSpec(this, _renderId, void 0);
    _classPrivateFieldInitSpec(this, _currentApp, void 0);
    _classPrivateFieldInitSpec(this, _previousApp, void 0);
    _classPrivateFieldInitSpec(this, _navConfig, void 0);
    _classPrivateFieldInitSpec(this, _bootstrapFailed, false);
    _classPrivateFieldSet(_storyboards, this, storyboards);
    const _history = getHistory();
    window.addEventListener("beforeunload", event => {
      const message = _assertClassBrand(_Router_brand, this, _getBlockMessageBeforePageLave).call(this, {});
      // See examples in https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
      if (message) {
        // Cancel the event
        // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        event.preventDefault();
        // Chrome requires returnValue to be set
        event.returnValue = "";
      } else {
        // the absence of a returnValue property on the event will guarantee the browser unload happens
        delete event.returnValue;
      }
    });
    _history.block((location, action) => _assertClassBrand(_Router_brand, this, _getBlockMessageBeforePageLave).call(this, {
      location,
      action
    }));
  }
  getRenderId() {
    return _classPrivateFieldGet(_renderId, this);
  }

  // istanbul ignore next
  getRuntimeContext() {
    return _classPrivateFieldGet(_runtimeContext, this);
  }
  getRecentApps() {
    return {
      currentApp: _classPrivateFieldGet(_currentApp, this),
      previousApp: _classPrivateFieldGet(_previousApp, this)
    };
  }
  getNavConfig() {
    return _classPrivateFieldGet(_navConfig, this);
  }
  bootstrap() {
    initAbortController();
    const history = getHistory();
    let nextPrevLocation = history.location;
    history.listen((location, action) => {
      const prevLocation = nextPrevLocation;
      nextPrevLocation = location;
      if (_classPrivateFieldGet(_rendering, this)) {
        _classPrivateFieldSet(_nextRender, this, {
          location,
          prevLocation,
          action
        });
      } else {
        _assertClassBrand(_Router_brand, this, _queuedRender).call(this, {
          location,
          prevLocation,
          action
        }).catch(handleHttpError);
      }
    });
    return _assertClassBrand(_Router_brand, this, _queuedRender).call(this, {
      location: history.location
    });
  }
}
function _getBlockMessageBeforePageLave(detail) {
  var _classPrivateFieldGet2;
  const history = getHistory();
  const previousMessage = history.getBlockMessage();
  (_classPrivateFieldGet2 = _classPrivateFieldGet(_rendererContext, this)) === null || _classPrivateFieldGet2 === void 0 || _classPrivateFieldGet2.dispatchBeforePageLeave(detail);
  const message = history.getBlockMessage();
  if (!previousMessage && message) {
    // Auto unblock only if new block was introduced by `onBeforePageLeave`.
    history.unblock();
  }
  return message;
}
function _safeRedirect(to, state, from) {
  var _this$redirectCount, _this$redirectCount2;
  if ((_classPrivateFieldSet(_redirectCount, this, (_this$redirectCount = _classPrivateFieldGet(_redirectCount, this), _this$redirectCount2 = _this$redirectCount++, _this$redirectCount)), _this$redirectCount2) > 10) {
    const message = `Infinite redirect detected: from "${from.pathname}${from.search}${from.hash}" to "${to}"`;
    // istanbul ignore else: error cannot be caught in test
    if (process.env.NODE_ENV === "test") {
      // eslint-disable-next-line no-console
      console.error(message);
      return;
    } else {
      throw new Error(message);
    }
  }
  getHistory().replace(to, state);
}
async function _queuedRender(next) {
  _classPrivateFieldSet(_rendering, this, true);
  try {
    await _assertClassBrand(_Router_brand, this, _preRender).call(this, next);
  } finally {
    _classPrivateFieldSet(_rendering, this, false);
    if (_classPrivateFieldGet(_nextRender, this)) {
      const nextRender = _classPrivateFieldGet(_nextRender, this);
      _classPrivateFieldSet(_nextRender, this, undefined);
      await _assertClassBrand(_Router_brand, this, _queuedRender).call(this, nextRender);
    }
  }
}
async function _preRender(_ref) {
  let {
    location,
    prevLocation,
    action
  } = _ref;
  if (prevLocation) {
    var _prevLocation$state, _location$state, _location$state2, _classPrivateFieldGet4;
    let ignoreRendering;
    const omittedLocationProps = {
      hash: undefined,
      state: undefined
    };
    // Omit the "key" when checking whether locations are equal in certain situations.
    if (
    // When current location is triggered by browser action of hash link.
    location.key === undefined ||
    // When current location is triggered by browser action of non-push-or-replace,
    // such as goBack or goForward,
    action === "POP" && (
    // and the previous location was triggered by hash link,
    prevLocation.key === undefined ||
    // or the previous location specified notify false.
    ((_prevLocation$state = prevLocation.state) === null || _prevLocation$state === void 0 ? void 0 : _prevLocation$state.notify) === false)) {
      omittedLocationProps.key = undefined;
    }
    if (locationsAreEqual({
      ...prevLocation,
      ...omittedLocationProps
    }, {
      ...location,
      ...omittedLocationProps
    }) || action !== "POP" && ((_location$state = location.state) === null || _location$state === void 0 ? void 0 : _location$state.notify) === false) {
      // Ignore rendering if location not changed except hash, state and optional key.
      // Ignore rendering if notify is `false`.
      ignoreRendering = true;
    }

    // Note: dot not perform incremental render when bootstrap failed.
    if (!ignoreRendering && !((_location$state2 = location.state) !== null && _location$state2 !== void 0 && _location$state2.noIncremental) && !_classPrivateFieldGet(_bootstrapFailed, this)) {
      var _classPrivateFieldGet3;
      ignoreRendering = await ((_classPrivateFieldGet3 = _classPrivateFieldGet(_rendererContext, this)) === null || _classPrivateFieldGet3 === void 0 ? void 0 : _classPrivateFieldGet3.didPerformIncrementalRender(location, prevLocation));
    }
    if (ignoreRendering) {
      return;
    }
    abortPendingRequest();
    (_classPrivateFieldGet4 = _classPrivateFieldGet(_rendererContext, this)) === null || _classPrivateFieldGet4 === void 0 || _classPrivateFieldGet4.dispatchPageLeave();
    if (action === "POP") {
      const storyboard = matchStoryboard(_classPrivateFieldGet(_storyboards, this), location.pathname);
      // When a browser action of goBack or goForward is performing,
      // force reload when the target page is a page of an outside app.
      if (isOutsideApp(storyboard)) {
        window.location.reload();
        return;
      }
    }
    devtoolsHookEmit("locationChange");
  }
  return _assertClassBrand(_Router_brand, this, _render).call(this, location, !prevLocation);
}
async function _render(location, isBootstrap) {
  var _hooks$flowApi, _hooks$pageView, _classPrivateFieldGet5, _storyboard$app, _hooks$auth, _hooks$auth$isBlocked;
  const renderId = _classPrivateFieldSet(_renderId, this, uniqueId("render-id-"));
  resetAllComputedMarks();
  clearResolveCache();
  hooks === null || hooks === void 0 || (_hooks$flowApi = hooks.flowApi) === null || _hooks$flowApi === void 0 || _hooks$flowApi.clearCollectWidgetContract();
  const history = getHistory();
  history.unblock();

  // const renderStartTime = performance.now();
  const finishPageView = hooks === null || hooks === void 0 || (_hooks$pageView = hooks.pageView) === null || _hooks$pageView === void 0 ? void 0 : _hooks$pageView.create();
  const storyboard = matchStoryboard(_classPrivateFieldGet(_storyboards, this), location.pathname);
  const previousApp = (_classPrivateFieldGet5 = _classPrivateFieldGet(_runtimeContext, this)) === null || _classPrivateFieldGet5 === void 0 ? void 0 : _classPrivateFieldGet5.app;
  const currentAppId = storyboard === null || storyboard === void 0 || (_storyboard$app = storyboard.app) === null || _storyboard$app === void 0 ? void 0 : _storyboard$app.id;
  //  dynamically change the value of the APP variable, if it's union app
  if (window.BOOTSTRAP_UNION_FILE && currentAppId && currentAppId !== (previousApp === null || previousApp === void 0 ? void 0 : previousApp.id)) {
    setAppVariable({
      appId: currentAppId,
      version: storyboard.app.currentVersion
    });
  }
  if (storyboard !== null && storyboard !== void 0 && storyboard.app) {
    await fulfilStoryboard(storyboard);
  }
  const currentApp = _classPrivateFieldSet(_currentApp, this, storyboard === null || storyboard === void 0 ? void 0 : storyboard.app);

  // Set `Router::#currentApp` before calling `getFeatureFlags()`
  // 必须在 currentApp 设置后调用，才能获取应用级的 feature flags
  const flags = getRuntime().getFeatureFlags();
  const blackListPreserveQueryFlag = flags["blacklist-preserve-query-string"];

  // 第一次检查：全局黑名单
  const pathToCheck = `${location.pathname}${blackListPreserveQueryFlag ? location.search : ""}`;
  let blocked = hooks === null || hooks === void 0 || (_hooks$auth = hooks.auth) === null || _hooks$auth === void 0 || (_hooks$auth$isBlocked = _hooks$auth.isBlockedPath) === null || _hooks$auth$isBlocked === void 0 ? void 0 : _hooks$auth$isBlocked.call(_hooks$auth, pathToCheck);
  if (currentApp) {
    var _storyboard$meta, _storyboard$meta$forE, _hooks$auth3, _hooks$auth3$isBlocke;
    storyboard === null || storyboard === void 0 || (_storyboard$meta = storyboard.meta) === null || _storyboard$meta === void 0 || (_storyboard$meta = _storyboard$meta.blackList) === null || _storyboard$meta === void 0 || (_storyboard$meta$forE = _storyboard$meta.forEach) === null || _storyboard$meta$forE === void 0 || _storyboard$meta$forE.call(_storyboard$meta, item => {
      var _hooks$auth2, _hooks$auth2$addPathT;
      let path = item && (item.to || item.url);
      if (!path || typeof path !== "string") return;

      // 保留查询字符串（如果特性开关启用）
      const pathParts = path.split("?");
      const pathWithoutQuery = pathParts[0];
      const queryString = blackListPreserveQueryFlag && pathParts[1] ? `?${pathParts[1]}` : "";
      path = pathWithoutQuery.replace(/\${\s*(?:(?:PATH|CTX)\.)?(\w+)\s*}/g, ":$1") + queryString;
      if (item.to) {
        try {
          path = computeRealValue(path, {
            app: currentApp
          });
        } catch (e) /* istanbul ignore next */{
          // eslint-disable-next-line no-console
          console.error(e);
        }
      } else {
        path = path.replace(/^\/next\//, "/");
      }
      path && path.startsWith("/") && (hooks === null || hooks === void 0 || (_hooks$auth2 = hooks.auth) === null || _hooks$auth2 === void 0 || (_hooks$auth2$addPathT = _hooks$auth2.addPathToBlackList) === null || _hooks$auth2$addPathT === void 0 ? void 0 : _hooks$auth2$addPathT.call(_hooks$auth2, path));
    });

    // 重新检查：全局黑名单 + 应用级黑名单
    blocked = hooks === null || hooks === void 0 || (_hooks$auth3 = hooks.auth) === null || _hooks$auth3 === void 0 || (_hooks$auth3$isBlocke = _hooks$auth3.isBlockedPath) === null || _hooks$auth3$isBlocke === void 0 ? void 0 : _hooks$auth3$isBlocke.call(_hooks$auth3, pathToCheck);
  }
  setWatermark();

  // Storyboard maybe re-assigned, e.g. when open launchpad.
  const appChanged = previousApp && currentApp ? previousApp.id !== currentApp.id : previousApp !== currentApp;
  clearExpressionASTCache();
  if (appChanged) {
    clearFunctionASTCache();
  }

  // TODO: handle favicon
  const prevRendererContext = _classPrivateFieldGet(_rendererContext, this);
  const redirectTo = (to, state) => {
    finishPageView === null || finishPageView === void 0 || finishPageView({
      status: "redirected"
    });
    _classPrivateFieldGet(_rendererContextTrashCan, this).add(prevRendererContext);
    _assertClassBrand(_Router_brand, this, _safeRedirect).call(this, to, state, location);
  };
  const redirectToLogin = () => {
    const to = flags["sso-enabled"] ? "/sso-auth/login" : "/auth/login";
    setLoginStateCookie(location);
    redirectTo(to, {
      from: location
    });
  };
  const main = document.querySelector("#main-mount-point");
  const portal = document.querySelector("#portal-mount-point");
  const renderRoot = {
    tag: RenderTag.ROOT,
    container: main,
    createPortal: portal
  };
  const cleanUpPreviousRender = () => {
    var _hooks$messageDispatc;
    unmountTree(main);
    unmountTree(portal);

    // Note: redirects can lead to multiple trash renderer contexts.
    _classPrivateFieldGet(_rendererContextTrashCan, this).add(prevRendererContext);
    for (const item of _classPrivateFieldGet(_rendererContextTrashCan, this)) {
      if (item) {
        item.dispatchOnUnmount();
        item.dispose();
      }
    }
    _classPrivateFieldGet(_rendererContextTrashCan, this).clear();
    hooks === null || hooks === void 0 || (_hooks$messageDispatc = hooks.messageDispatcher) === null || _hooks$messageDispatc === void 0 || _hooks$messageDispatc.reset();
    if (appChanged) {
      _classPrivateFieldSet(_previousApp, this, previousApp);
      window.dispatchEvent(new CustomEvent("app.change", {
        detail: {
          previousApp,
          currentApp
        }
      }));
    }
  };
  setTheme(currentApp && (getLocalAppsTheme()[currentApp.id] || currentApp.theme) || "light");
  setMode("default");
  setThemeVariant(getRuntime().getMiscSettings().globalThemeVariant);
  if (currentApp && !blocked) {
    var _hooks$checkInstalled, _hooks$auth4, _hooks$checkPermissio, _storyboard$meta2, _hooks$flowApi2, _storyboard$meta3;
    hooks === null || hooks === void 0 || (_hooks$checkInstalled = hooks.checkInstalledApps) === null || _hooks$checkInstalled === void 0 || _hooks$checkInstalled.preCheckInstalledApps(storyboard, appId => !!_internalApiGetAppInBootstrapData(appId));
    const routeHelper = {
      bailout: output => {
        if (output.unauthenticated) {
          redirectToLogin();
          return true;
        }
        if (output.redirect) {
          redirectTo(output.redirect.path, output.redirect.state);
          return true;
        }
        // Reset redirect count if no redirect is set.
        _classPrivateFieldSet(_redirectCount, this, 0);
      },
      mergeMenus: async menuRequests => {
        const menuConfs = await Promise.all(menuRequests);
        _classPrivateFieldSet(_navConfig, this, mergeMenuConfs(menuConfs));
        window.dispatchEvent(new CustomEvent("navConfig.change", {
          detail: _classPrivateFieldGet(_navConfig, this)
        }));
      },
      catch: async (error, returnNode, isCurrentBootstrap, isReCatch) => {
        if (isUnauthenticatedError(error) && !window.NO_AUTH_GUARD) {
          redirectToLogin();
          return;
        } else if (error instanceof HttpAbortError) {
          _classPrivateFieldGet(_rendererContextTrashCan, this).add(prevRendererContext);
          return;
        } else {
          const noAuthGuardLoginPath = getRuntime().getMiscSettings().noAuthGuardLoginPath;
          if (isUnauthenticatedError(error) && noAuthGuardLoginPath) {
            redirectTo(noAuthGuardLoginPath, {
              from: location
            });
            return;
          }
          if (isCurrentBootstrap) {
            throw error;
          }
          if (!isReCatch && shouldReloadForError(error)) {
            window.location.reload();
            return;
          }
          return {
            failed: true,
            output: {
              node: await ErrorNode(error, returnNode, !isReCatch),
              blockingList: []
            }
          };
        }
      }
    };
    const rendererContext = _classPrivateFieldSet(_rendererContext, this, new RendererContext("page", {
      routeHelper,
      renderId
    }));
    const runtimeContext = _classPrivateFieldSet(_runtimeContext, this, {
      app: currentApp,
      location,
      query: new URLSearchParams(location.search),
      flags,
      sys: {
        ...(hooks === null || hooks === void 0 || (_hooks$auth4 = hooks.auth) === null || _hooks$auth4 === void 0 ? void 0 : _hooks$auth4.getAuth()),
        ...getPageInfo(),
        settings: {
          brand: getRuntime().getBrandSettings()
        }
      },
      ctxStore: new DataStore("CTX", undefined, rendererContext),
      pendingPermissionsPreCheck: [hooks === null || hooks === void 0 || (_hooks$checkPermissio = hooks.checkPermissions) === null || _hooks$checkPermissio === void 0 ? void 0 : _hooks$checkPermissio.preCheckPermissions(storyboard)],
      tplStateStoreMap: new Map(),
      formStateStoreMap: new Map()
    });
    _classPrivateFieldSet(_navConfig, this, undefined);
    registerCustomTemplates(storyboard);
    registerStoryboardFunctions((_storyboard$meta2 = storyboard.meta) === null || _storyboard$meta2 === void 0 ? void 0 : _storyboard$meta2.functions, currentApp);
    hooks === null || hooks === void 0 || (_hooks$flowApi2 = hooks.flowApi) === null || _hooks$flowApi2 === void 0 || _hooks$flowApi2.collectContract((_storyboard$meta3 = storyboard.meta) === null || _storyboard$meta3 === void 0 ? void 0 : _storyboard$meta3.contracts);
    let failed = false;
    let output;
    let stores = [];
    try {
      clearMatchedRoutes();
      const rootMenuRequestNode = {};
      output = await renderRoutes(renderRoot, insertPreviewRoutes(storyboard.routes), runtimeContext, rendererContext, [], rootMenuRequestNode);
      if (routeHelper.bailout(output)) {
        return;
      }
      stores = getDataStores(runtimeContext);
      await postAsyncRender(output, runtimeContext, stores);
      rootMenuRequestNode.child = output.menuRequestNode;
      rendererContext.setInitialMenuRequestNode(rootMenuRequestNode);
      await routeHelper.mergeMenus(rendererContext.getMenuRequests());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Router failed:", error);
      if (isBootstrap) {
        _classPrivateFieldSet(_bootstrapFailed, this, true);
      }
      const result = await routeHelper.catch(error, renderRoot, isBootstrap);
      if (!result) {
        return;
      }
      ({
        failed,
        output
      } = result);
    }
    resetReloadForError();
    renderRoot.child = output.node;
    _classPrivateFieldSet(_bootstrapFailed, this, false);
    cleanUpPreviousRender();
    if (output.route && output.route.type !== "routes" || failed) {
      if (!failed) {
        // There is a window to set theme and mode by `lifeCycle.onBeforePageLoad`.
        rendererContext.dispatchBeforePageLoad();
      }
      applyTheme();
      applyMode();
      setUIVersion(currentApp === null || currentApp === void 0 ? void 0 : currentApp.uiVersion);
      mountTree(renderRoot);

      // Scroll to top after each rendering.
      // See https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/docs/guides/scroll-restoration.md
      window.scrollTo(0, 0);
      if (!failed) {
        rendererContext.dispatchPageLoad();
        rendererContext.dispatchAnchorLoad();
        rendererContext.dispatchOnMount();
        rendererContext.initializeScrollIntoView();
        rendererContext.initializeMediaChange();
        rendererContext.initializeMessageDispatcher();
        for (const store of stores) {
          store.mountAsyncData();
        }
        finishPageView === null || finishPageView === void 0 || finishPageView({
          status: "ok",
          path: output.path,
          pageTitle: document.title
        });
      } else {
        finishPageView === null || finishPageView === void 0 || finishPageView({
          status: "failed"
        });
      }
      devtoolsHookEmit("rendered");
      return;
    }
  } else if (!window.NO_AUTH_GUARD && hooks !== null && hooks !== void 0 && hooks.auth && !hooks.auth.isLoggedIn()) {
    // Todo(steve): refine after api-gateway supports fetching storyboards before logged in.
    // Redirect to login if no storyboard is matched.
    redirectToLogin();
    return;
  } else {
    cleanUpPreviousRender();
  }
  applyTheme();
  applyMode();
  const node = await ErrorNode(new PageError(blocked ? "page blocked" : currentApp ? "page not found" : "app not found"), renderRoot, true);
  renderRoot.child = node;
  mountTree(renderRoot);

  // Scroll to top after each rendering.
  window.scrollTo(0, 0);
  finishPageView === null || finishPageView === void 0 || finishPageView({
    status: blocked ? "blocked" : "not-found"
  });
  devtoolsHookEmit("rendered");
}
function mergeMenuConfs(menuConfs) {
  const navConfig = {
    breadcrumb: []
  };
  for (const menuConf of menuConfs) {
    const {
      breadcrumb
    } = menuConf;
    if (breadcrumb) {
      if (breadcrumb.overwrite) {
        navConfig.breadcrumb = breadcrumb.items;
      } else {
        navConfig.breadcrumb.push(...breadcrumb.items);
      }
    }
  }
  return navConfig;
}
//# sourceMappingURL=Router.js.map